/**
 * NMS Store Launch · Store directory
 * Search + state filter over the stores, newest first, grouped by chapter (launch year).
 * Desktop/tablet shows the active store in a sticky card (photo carousel,
 * facts, actions); mobile opens the shared bottom sheet. Follows the
 * page-wide active store (map, timeline, nearest store and shared #store-NN links).
 */
(() => {
  const PIN = /^\d{6}$/; // Indian PIN code

  class NMSSlDirectory extends window.NMSBaseClass {
    constructor(element, service) {
      super();
      this.element = element;
      this.sl = service;
      this.labels = element.dataset;
      this.controller = new AbortController();
      this.state = 'All';
      this.query = '';
      this.limit = +element.dataset.initialCount || 0; // 0 = show every store
      this.expanded = false;
    }

    init() {
      super.init();
      const { signal } = this.controller;
      this.stores = this.sl.stores.filter(s => !s.mock);
      this.#renderCount();
      this.#renderCities();
      this.#renderList();
      this.#renderDetail();

      this.refs.search.addEventListener('input', () => {
        this.query = this.refs.search.value.trim().toLowerCase();
        this.byDistance = false;
        // A PIN code searches every state, so the chips go back to "All".
        if (PIN.test(this.query) && this.state !== 'All') {
          this.state = 'All';
          this.#renderCities();
        }
        this.#renderList();
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.#trackSearch(), 900);
      }, { signal });
      this.refs.near.addEventListener('click', () => this.sl.nearMe(), { signal });
      this.#rotateSearchHint();
      this.refs.cities.addEventListener('click', event => {
        const tab = event.target.closest('[data-state]');
        if (!tab) return;
        this.state = tab.dataset.state;
        this.byDistance = false;
        this.#renderCities();
        this.#renderList();
      }, { signal });
      this.refs.list.addEventListener('click', event => {
        const row = event.target.closest('[data-i]');
        if (!row) return;
        const i = +row.dataset.i;
        if (this.sl.isMobile()) this.sl.openStore(i, 'dir');
        else this.sl.select(i, 'directory');
      }, { signal });
      this.refs.detail.addEventListener('click', event => this.#onDetailClick(event), { signal });
      this.refs.viewAll?.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.#renderList();
        if (!this.expanded) this.refs.listWrap.scrollTo({ top: 0 });
      }, { signal });

      this.sl.on('select', ({ index, source }) => {
        if (source === 'location') this.#showNearest(index);
        else this.#syncActive(source !== 'directory');
        this.#renderDetail();
      }, signal);
      this.sl.on('location', ({ busy, message, html }) => {
        this.refs.nearLabel.textContent = busy ? this.sl.t.locating : this.sl.t.findNearest;
        if (html) this.refs.locMsg.innerHTML = html;
        else this.refs.locMsg.textContent = message || '';
      }, signal);
      this.sl.on('tick', () => this.#renderCount(), signal);
    }

    #renderCount() {
      const T = this.sl.totals();
      const L = this.sl.launch;
      this.refs.countLine.textContent = this.sl.helpers.fill(this.sl.isLive() ? this.labels.countLive : this.labels.countCountdown, {
        stores: T.stores,
        open: T.open,
        states: T.states,
        number: L.number,
        date: L.dateLong,
      });
    }

    #renderCities() {
      const { esc } = this.sl.helpers;
      const counts = {};
      this.stores.forEach(s => s.state && (counts[s.state] = (counts[s.state] || 0) + 1));
      const tabs = [['All', this.labels.allLabel, this.stores.length], ...Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0])).map(([c, n]) => [c, c, n])];
      this.refs.cities.innerHTML = tabs.map(([value, label, n]) => `
        <button type="button" role="tab" class="nms-sl-chip" data-state="${esc(value)}" aria-selected="${value === this.state}">
          ${this.#stateIcon(value === 'All' ? '' : value)}<span class="nms-sl-chip__label">${esc(label)}</span><span class="nms-sl-chip__count">${n}</span>
        </button>`).join('');
    }

    /** State icon badge (Launch · Map "State icon" blocks); All and states without one get the pin. */
    #stateIcon(name) {
      const { esc, icon } = this.sl.helpers;
      const src = name && this.sl.stateIcon(name);
      return `<span class="nms-sl-chip__icon" aria-hidden="true">${src ? `<img src="${esc(src)}" alt="" width="22" height="22" loading="lazy">` : icon('pin', 18)}</span>`;
    }

    #tag(s) {
      return s.isLaunch && s.tag ? `<span class="nms-sl-dir__row-tag">${this.sl.helpers.esc(s.tag)}</span>` : '';
    }

    #renderList() {
      const { esc, fill } = this.sl.helpers;
      const t = this.sl.t;
      const q = this.query;
      const pin = PIN.test(q);
      const list = pin ? this.#byPin(q) : this.stores.filter(s =>
        (this.state === 'All' || s.state === this.state) &&
        (!q || [s.title, s.city, s.state, s.num, s.chapter, s.zip].join(' ').toLowerCase().includes(q)));
      const here = this.sl.userLocation;
      if (pin) {
        // Already ordered by closeness of the PIN code.
      } else if (this.byDistance && here) {
        const away = st => (st.hasGeo ? this.sl.km(here.lat, here.lng, st.lat, st.lng) : Infinity);
        list.sort((a, b) => away(a) - away(b));
      } else {
        // Newest first: the launch store, then each launch year down to "Where it began".
        list.sort((a, b) => b.i - a.i);
      }
      const perChapter = {};
      list.forEach(s => (perChapter[s.chapter] = (perChapter[s.chapter] || 0) + 1));
      const active = this.sl.activeIndex;
      // First `limit` stores until "View all stores"; a search always lists every match.
      const limited = this.limit > 0 && !q && list.length > this.limit;
      const shown = limited && !this.expanded ? list.slice(0, this.limit) : list;
      if (this.refs.more) {
        this.refs.more.hidden = !limited;
        this.refs.viewAll.setAttribute('aria-expanded', String(this.expanded));
        this.refs.viewAllLabel.textContent = this.expanded ? this.labels.viewLess : this.labels.viewAll;
      }
      this.refs.list.innerHTML = shown.map((s, k) => {
        // PIN results are ordered by closeness, not year: no year headings.
        const header = !pin && (k === 0 || shown[k - 1].chapter !== s.chapter)
          ? `<div class="nms-sl-dir__group" role="presentation"><span class="nms-sl-dir__group-name">${esc(s.chapter)}</span><span class="nms-sl-dir__group-count">${esc(perChapter[s.chapter] === 1 ? this.labels.storeOne : fill(this.labels.storeMany, { count: perChapter[s.chapter] }))}</span></div>`
          : '';
        const mod = s.isLaunch ? ' nms-sl-dir__row--launch' : s.isFirst ? ' nms-sl-dir__row--first' : '';
        return `${header}<div role="listitem">
          <button type="button" class="nms-sl-dir__row${mod}" data-i="${s.i}" aria-pressed="${s.i === active}">
            <span class="nms-sl-dir__dot" aria-hidden="true"></span>
            <span class="nms-sl-dir__row-text">
              <span class="nms-sl-dir__row-stop">${esc(fill(t.stop, { num: s.num }))} · ${esc(s.stateCode)}</span>
              <span class="nms-sl-dir__row-line"><span class="nms-sl-dir__row-title">${esc(s.title)}</span>${this.#tag(s)}</span>
              <span class="nms-sl-dir__row-place">${esc(s.place)}</span>
            </span>
          </button>
        </div>`;
      }).join('');
      this.refs.empty.hidden = list.length > 0;
      this.resultCount = list.length;
    }

    /** "Find nearest": open the store's state chip, clear the search and bring the store to the top. */
    #showNearest(index) {
      const store = this.sl.stores[index];
      if (!store || store.mock) return;
      this.state = this.stores.some(s => s.state === store.state) ? store.state : 'All';
      this.query = '';
      this.refs.search.value = '';
      this.byDistance = true;
      this.#renderCities();
      this.#renderList();

      const smooth = this.sl.reducedMotion ? 'auto' : 'smooth';
      const scroller = this.refs.cities;
      const chip = scroller.querySelector('[aria-selected="true"]');
      if (chip) scroller.scrollTo({ left: scroller.scrollLeft + chip.getBoundingClientRect().left - scroller.getBoundingClientRect().left - 8, behavior: smooth });
      this.refs.listWrap.scrollTo({ top: 0, behavior: smooth });

      // The nearest store is now the first row; bring it on screen only if it isn't.
      const row = this.refs.list.querySelector(`[data-i="${index}"]`);
      // The automatic check on arrival must not pull the page down to this section.
      const rect = row?.getBoundingClientRect();
      if (!this.sl.locationQuiet && rect && (rect.top < 0 || rect.bottom > window.innerHeight)) row.scrollIntoView({ block: 'nearest', behavior: smooth });
    }

    /**
     * Stores near a 6-digit PIN code, from the store data only. Indian PIN codes are hierarchical:
     * the first 3 digits are the sorting district (360xxx = Rajkot), 2 the postal area, 1 the region.
     * Use the narrowest level that has stores; exact PIN first, then the numerically closest.
     */
    #byPin(pin) {
      const zip = s => String(s.zip || '').trim();
      for (const digits of [3, 2, 1]) {
        const matches = this.stores.filter(s => zip(s).slice(0, digits) === pin.slice(0, digits));
        if (matches.length) return matches.sort((a, b) => Math.abs(+zip(a) - +pin) - Math.abs(+zip(b) - +pin));
      }
      return [];
    }

    #syncActive(scrollIntoList) {
      const active = this.sl.activeIndex;
      // A store picked elsewhere (map, timeline) that sits beyond the first few: open the full list.
      if (scrollIntoList && !this.expanded && !this.refs.list.querySelector(`[data-i="${active}"]`) &&
        this.stores.some(s => s.i === active && (this.state === 'All' || s.state === this.state))) {
        this.expanded = true;
        this.#renderList();
      }
      let row = null;
      this.refs.list.querySelectorAll('[data-i]').forEach(el => {
        const on = +el.dataset.i === active;
        el.setAttribute('aria-pressed', String(on));
        if (on) row = el;
      });
      if (row && scrollIntoList) {
        // Scroll only the list box, never the page.
        const wrap = this.refs.listWrap;
        const top = row.offsetTop - wrap.clientHeight / 2 + row.clientHeight / 2;
        wrap.scrollTo({ top, behavior: this.sl.reducedMotion ? 'auto' : 'smooth' });
      }
    }

    #renderDetail() {
      const s = this.sl.stores[this.sl.activeIndex];
      if (!s) return;
      const { esc, icon, fill } = this.sl.helpers;
      const t = this.sl.t;
      const L = this.labels;
      const distance = this.sl.distanceText(s);
      const link = (cls, href, iconName, label) =>
        href ? `<a class="nms-sl-dir__btn ${cls}" href="${esc(href)}"${/^https?:/.test(href) ? ' target="_blank" rel="noopener"' : ''}>${icon(iconName, 16)}<span>${esc(label)}</span></a>` : '';
      const photos = s.images.length
        ? `<div class="nms-sl-dir__photos" data-photos role="group" aria-label="${esc(L.galleryLabel)}">${s.images.map((img, k) => `<img src="${esc(img.src)}" alt="${esc(img.alt || `${s.title} ${k + 1}`)}" width="263" height="350" loading="lazy" decoding="async">`).join('')}</div>
          <div class="nms-sl-dir__photo-nav" data-photo-nav hidden>
            <button type="button" class="nms-sl-dir__photo-btn" data-photo-step="-1" aria-label="${esc(L.labelPrev)}" disabled>${icon('caret-left', 20)}</button>
            <span class="nms-sl-dir__indicator" aria-hidden="true"><span data-photo-thumb></span></span>
            <button type="button" class="nms-sl-dir__photo-btn" data-photo-step="1" aria-label="${esc(L.labelNext)}">${icon('caret-right', 20)}</button>
          </div>`
        : `<div class="nms-sl-dir__placeholder nms-sl-placeholder" style="--tone:${s.tone}"><span class="nms-sl-placeholder__note">${esc(s.photoNote)}</span></div>`;

      this.refs.detail.innerHTML = `${photos}
        <div class="nms-sl-dir__d-head">
          <span class="nms-sl-dir__d-stop">${esc(fill(t.stop, { num: s.num }))} · ${esc(s.chapter)}</span>
          <h3 class="nms-sl-dir__d-title">${esc(s.title)}</h3>
          <span class="nms-sl-dir__d-place">${esc(s.place)}</span>
          ${distance ? `<span class="nms-sl-dir__d-dist">${esc(distance)}</span>` : ''}
        </div>
        <dl class="nms-sl-dir__facts">
          <div class="nms-sl-dir__fact"><dt>${icon('pin', 16)}${esc(L.labelAddress)}</dt><dd>${esc(s.addressFull || t.notListed)}</dd></div>
          <div class="nms-sl-dir__fact"><dt>${icon('clock', 16)}${esc(L.labelHours)}</dt><dd>${esc(s.hoursText)}</dd></div>
          <div class="nms-sl-dir__fact"><dt>${icon('star-outline', 16)}${esc(L.labelReviews)}</dt><dd data-sl-rating="${s.i}"></dd></div>
        </dl>
        <div class="nms-sl-dir__actions">
          ${link('nms-sl-dir__btn--dark', s.map, 'navigate', L.labelDirections)}
          ${link('', s.waUrl, 'whatsapp', L.labelWhatsapp)}
          ${link('', s.tel, 'phone', L.labelCall)}
          <button type="button" class="nms-sl-dir__btn" data-share>${icon('share', 16)}<span>${esc(L.labelShare)}</span></button>
        </div>`;
      this.#bindPhotos();
      this.sl.fillRatings(this.refs.detail);
    }

    /** Photo strip: arrows step one photo, indicator follows the scroll. */
    #bindPhotos() {
      const strip = this.refs.detail.querySelector('[data-photos]');
      const nav = this.refs.detail.querySelector('[data-photo-nav]');
      if (!strip || !nav) return;
      const thumb = nav.querySelector('[data-photo-thumb]');
      const [prev, next] = nav.querySelectorAll('[data-photo-step]');
      const update = () => {
        const max = strip.scrollWidth - strip.clientWidth;
        nav.hidden = max <= 2;
        if (max <= 2) return;
        const progress = Math.min(1, Math.max(0, strip.scrollLeft / max));
        const thumbWidth = Math.max(16, Math.round((64 * strip.clientWidth) / strip.scrollWidth));
        thumb.style.setProperty('--thumb', `${thumbWidth}px`);
        thumb.style.setProperty('--offset', `${((64 - thumbWidth) * progress).toFixed(1)}px`);
        prev.disabled = strip.scrollLeft <= 2;
        next.disabled = strip.scrollLeft >= max - 2;
      };
      strip.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true, signal: this.controller.signal });
      strip.querySelectorAll('img').forEach(img => img.addEventListener('load', update, { once: true }));
      requestAnimationFrame(update);
    }

    async #onDetailClick(event) {
      const step = event.target.closest('[data-photo-step]');
      if (step) {
        const strip = this.refs.detail.querySelector('[data-photos]');
        const photo = strip?.querySelector('img');
        if (strip && photo) strip.scrollBy({ left: +step.dataset.photoStep * (photo.getBoundingClientRect().width + 12), behavior: this.sl.reducedMotion ? 'auto' : 'smooth' });
        return;
      }
      const share = event.target.closest('[data-share]');
      if (share) {
        const copied = await this.sl.shareStore(this.sl.stores[this.sl.activeIndex]);
        if (copied) share.querySelector('span').textContent = this.sl.t.linkCopied;
      }
    }

    /** Header-search style hint: the word after "Search" changes every 2s (slides up out, in from below). */
    #rotateSearchHint() {
      const word = this.refs.searchWord;
      const words = (word?.dataset.words || '').split('|').map(w => w.trim()).filter(Boolean);
      if (!word || words.length < 2) return;
      let k = 0;
      const swap = () => {
        const next = () => {
          k = (k + 1) % words.length;
          word.textContent = words[k];
        };
        if (this.sl.reducedMotion) return next();
        word.classList.add('is-exiting');
        setTimeout(() => {
          next();
          word.classList.replace('is-exiting', 'is-entering');
          void word.offsetWidth;
          word.classList.remove('is-entering');
        }, 100);
      };
      // Only while the field is on screen and showing the hint.
      const input = this.refs.search;
      let visible = false;
      this.hintObserver = new IntersectionObserver(([entry]) => (visible = entry.isIntersecting));
      this.hintObserver.observe(word);
      this.hintTimer = setInterval(() => {
        if (visible && !input.value && document.activeElement !== input) swap();
      }, 2000);
    }

    #trackSearch() {
      if (!this.query) return;
      this.sl.track(this.resultCount === 0 ? 'store_search_no_results' : 'store_search', { q: this.query, results: this.resultCount });
    }

    destroy() {
      this.controller.abort();
      clearTimeout(this.searchTimer);
      clearInterval(this.hintTimer);
      this.hintObserver?.disconnect();
      super.destroy();
    }
  }

  const register = () => window.neemans.storeLaunch.register('[data-nms-sl-directory]', NMSSlDirectory);
  if (window.neemans?.storeLaunch) register();
  else document.addEventListener('nms-sl:ready', register, { once: true });
})();
